import React, {useState, useEffect, useCallback} from 'react'
import {
  Row,
  Col,
  Button,
  Upload,
  message,
  Card,
  Table,
  Modal,
  Form,
  Input,
  Tooltip,
  Popconfirm,
} from 'antd'
import * as XLSX from 'xlsx'
import {saveAs} from 'file-saver'
import {
  PlusOutlined,
  UploadOutlined,
  FileExcelOutlined,
  CloudUploadOutlined,
  CloudDownloadOutlined,
} from '@ant-design/icons'
import apiRequest from '../hooks/ApiRequest'
import CommonSearch from '../hooks/Search'

function BulkUpload({form}) {
  const {getFieldDecorator, validateFields, resetFields, setFieldsValue} = form

  /* ──────────────── state ──────────────── */
  const [excelData, setExcelData] = useState([])
  const [vehicles, setVehicles] = useState([])
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [mode, setMode] = useState('list') // 'list' | 'import'
  const [editingRecord, setEditingRecord] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  useEffect(() => {
    fetchVehicles()
  }, [])

  const fetchVehicles = useCallback(async () => {
    setLoading(true)
    try {
      const url = `${process.env.REACT_APP_BASE_URL}/api/MotorFleet/get-motorFleet`
      const res = await apiRequest(url, 'GET')
      // debugger;
      if (res.data?.status !== true) {
        message.error(res?.message || 'Could not load vehicles')
        return
      }
      setExcelData(res.data.data) // this drives the table
    } catch {
      message.error('Error loading vehicles')
    } finally {
      setLoading(false)
    }
  }, [])

  /* ──────────────── table columns ──────────────── */
  const columns = [
    {
      title: 'Vehicle Registration',
      dataIndex: 'vehicleRegistration',
      key: 'vehicleRegistration',
    },
    {title: 'Make', dataIndex: 'make', key: 'make'},
    {title: 'Model', dataIndex: 'model', key: 'model'},
    {title: 'Year', dataIndex: 'year', key: 'year'},
    {title: 'Sum Insured', dataIndex: 'sumInsured', key: 'sumInsured'},

    {
      title: 'Actions',
      key: 'actions',
      render: (_, record, index) => (
        <>
          <Tooltip title="Edit">
            <Button
              type="primary"
              icon="edit"
              onClick={() => handleEdit(record, index)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure to delete this vehicle?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No">
            <Tooltip title="Delete">
              <Button type="danger" icon="delete" style={{marginLeft: 8}} />
            </Tooltip>
          </Popconfirm>
        </>
      ),
    },
  ]

  const handleUpload = async ({file, onSuccess, onError}) => {
    try {
      const formData = new FormData()
      formData.append('ExcelFile', file)

      const reader = new FileReader()
      reader.onload = async e => {
        try {
          const data = new Uint8Array(e.target.result)
          const workbook = XLSX.read(data, {type: 'array'})
          const worksheet = workbook.Sheets[workbook.SheetNames[0]]
          const parsedData = XLSX.utils.sheet_to_json(worksheet)

          if (!parsedData.length) {
            message.error('No data found in Excel')
            return
          }

          const newData = [...excelData, ...parsedData]
          setMode('list') // back to list view
          // localStorage.setItem('motorFleetData', JSON.stringify(newData))
debugger;
          const url = `${process.env.REACT_APP_BASE_URL}/api/MotorFleet/upload-motorFleet`
          const res = await apiRequest(url, 'POST', formData)

          if (res.success) {
            setExcelData(newData)
            message.success('File uploaded successfully')
            onSuccess(null, file)
          } else {
            throw new Error(res.error || 'Upload failed')
          }
        } catch (err) {
          message.error(err.message || 'Upload failed')
          onError(err)
        }
      }
      reader.readAsArrayBuffer(file)
    } catch (error) {
      message.error('Upload failed')
      onError(error)
    }
  }

  const handleDownloadSample = () => {
    const headers = [
      ['vehicleRegistration', 'make', 'model', 'year', 'sumInsured'],
    ]
    const worksheet = XLSX.utils.aoa_to_sheet(headers)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'MotorFleetData')
    const wbout = XLSX.write(workbook, {bookType: 'xlsx', type: 'array'})
    const blob = new Blob([wbout], {type: 'application/octet-stream'})
    saveAs(blob, 'MotorFleetSampleHeaders.xlsx')
  }

  const handleClearData = () => {
    setExcelData([])
    localStorage.removeItem('motorFleetData')
    message.success('Data cleared')
  }

  /* ---------- save (add / update) ---------- */
  const handleAddSubmit = () => {
    validateFields(async (err, values) => {
      if (err) return

      const payload = {
        vehicleRegistration: values.VehicleRegistration,
        make: values.Make,
        model: values.Model,
        year: Number(values.Year),
        sumInsured: Number(values.SumInsured),
      }

      try {
        setLoading(true)

        if (editingRecord) {
          // UPDATE
          const url = `${process.env.REACT_APP_BASE_URL}/api/MotorFleet/update-motorFleet/${editingRecord.id}`
          const res = await apiRequest(url, 'POST', payload) // <‑‑ object, not string
          if (res.data?.status !== true) {
            throw new Error(res.data?.message || 'Update failed')
          }
          message.success('Vehicle updated')
        } else {
          // ADD
          const url = `${process.env.REACT_APP_BASE_URL}/api/MotorFleet/add-motorFleet`
          const res = await apiRequest(url, 'POST', payload) // <‑‑ object, not string
          if (res.data?.status !== true) {
            throw new Error(res.data?.message || 'Add failed')
          }
          message.success('Vehicle added')
        }

        setModalVisible(false)
        setEditingRecord(null)
        resetFields()
        fetchVehicles()
      } catch (e) {
        message.error(e?.response?.data?.message || e.message || 'Save failed')
      } finally {
        setLoading(false)
      }
    })
  }

  /* ---------- delete ---------- */
  const handleDelete = async id => {
    try {
      setLoading(true)
      const url = `${process.env.REACT_APP_BASE_URL}/api/MotorFleet/delete-motorFleet/${id}`
      await apiRequest(url, 'POST')
      // debugger;
      message.success('Vehicle deleted')
      fetchVehicles()
    } catch (e) {
      message.error(e?.response?.data?.message || e.message || 'Delete failed')
    } finally {
      setLoading(false)
    }
  }

  /* ---------- edit button ---------- */
  const handleEdit = record => {
    setModalVisible(true)
    setEditingRecord(record)
    setFieldsValue({
      VehicleRegistration: record.vehicleRegistration,
      Make: record.make,
      Model: record.model,
      Year: record.year,
      SumInsured: record.sumInsured,
    })
  }
  const handleSearch = value => {
    const filterTable = excelData.filter(o =>
      Object.keys(o).some(k =>
        String(o[k]).toLowerCase().includes(value.toLowerCase()),
      ),
    )
    setExcelData(filterTable)
  }
  const ClearSerach = () => {
    setSearchTerm('')
    setExcelData(excelData)
    fetchVehicles()
  }

  return (
    <div title="Claim Limits" className="ant-layout-content dashboard">
      {/*  LIST  */}
      {mode === 'list' && (
        <>
          {/* header */}
          <div className="dash-bg-white">
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={24} md={24} lg={6} xl={12}>
                <div className="section-top-heading">
                  <h3
                    style={{
                      color: '#0C1362',
                      fontWeight: '600',
                      fontSize: '20px',
                    }}>
                    Motor Fleet List
                  </h3>
                </div>
              </Col>
              <Col xs={24} sm={24} md={12} lg={8} xl={4}>
                <CommonSearch
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  onSearch={handleSearch}
                  onClear={ClearSerach}
                />
              </Col>
              <Col xs={10} sm={10} md={6} lg={3} xl={2}>
                <Button type="primary" onClick={() => handleSearch(searchTerm)}>
                  Search
                </Button>
              </Col>
              <Col xs={14} sm={14} md={6} lg={5} xl={3}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setModalVisible(true)}
                  style={{marginRight: 8}}>
                  Add New
                </Button>
              </Col>
              <Col xs={14} sm={14} md={6} lg={5} xl={2}>
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  onClick={() => setMode('import')}>
                  Import
                </Button>
              </Col>
            </Row>
          </div>

          {/* table */}
          <div>
            <Table
              loading={loading}
              dataSource={excelData}
              columns={columns}
              rowKey="id" // or "id"
              // bordered
              pagination={{pageSize: 10}}
            />
          </div>
        </>
      )}

      {/*  IMPORT  */}
      {mode === 'import' && (
        <div className="dash-bg-white" style={{padding: 24, marginTop: 20}}>
          <h3
            style={{
              color: '#0C1362',
              fontWeight: '600',
              fontSize: '20px',
            }}>
            MOTOR FLEET BULK UPLOAD
          </h3>
          <Button
            type="default"
            onClick={() => setMode('list')}
            style={{
              borderColor: '#0C1362',
              color: '#0C1362',
              fontWeight: 500,
              borderRadius: 6,
            }}>
            ← Back to List
          </Button>

          <Row gutter={24} justify="center" style={{marginTop: 20}}>
            <Col xs={24} sm={24} md={12}>
              <Card
                title={
                  <span>
                    <FileExcelOutlined
                      style={{marginRight: 8, color: '#52c41a'}}
                    />
                    Upload Filled Excel File
                  </span>
                }
                bodyStyle={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 120,
                }}>
                <Upload customRequest={handleUpload} showUploadList={false}>
                  <Button type="primary">
                    <CloudUploadOutlined /> Click to Upload
                  </Button>
                </Upload>
              </Card>
            </Col>

            <Col xs={24} sm={24} md={12}>
              <Card
                title={
                  <span>
                    <CloudDownloadOutlined
                      style={{marginRight: 8, color: '#52c41a'}}
                    />
                    Download Bulk Upload Template
                  </span>
                }
                bodyStyle={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 120,
                }}>
                <Button type="primary" onClick={handleDownloadSample}>
                  <CloudDownloadOutlined /> Download Template
                </Button>
              </Card>
            </Col>
          </Row>
        </div>
      )}

      {/*  ADD / EDIT MODAL  */}
      <Modal
        title={`${editingRecord !== null ? 'Edit' : 'Add New'} Vehicle`}
        visible={modalVisible}
        onCancel={() => {
          setModalVisible(false)
          setEditingRecord(null)
          resetFields()
        }}
        onOk={handleAddSubmit}
        okText="Submit">
        <Form layout="vertical">
          <Form.Item label="Vehicle Registration">
            {getFieldDecorator('VehicleRegistration', {
              rules: [
                {required: true, message: 'Please enter vehicle registration'},
              ],
            })(<Input />)}
          </Form.Item>
          <Form.Item label="Make">
            {getFieldDecorator('Make', {
              rules: [{required: true, message: 'Please enter make'}],
            })(<Input />)}
          </Form.Item>
          <Form.Item label="Model">
            {getFieldDecorator('Model', {
              rules: [{required: true, message: 'Please enter model'}],
            })(<Input />)}
          </Form.Item>
          <Form.Item label="Year">
            {getFieldDecorator('Year', {
              rules: [
                {required: true, message: 'Please enter year'},
                {pattern: /^\d{4}$/, message: 'Enter valid 4‑digit year'},
              ],
            })(<Input />)}
          </Form.Item>
          <Form.Item label="Sum Insured">
            {getFieldDecorator('SumInsured', {
              rules: [
                {required: true, message: 'Please enter sum insured'},
                {pattern: /^\d+$/, message: 'Only numbers allowed'},
              ],
            })(<Input />)}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default Form.create()(BulkUpload)
